<template>
  <div class="w-full px-3 py-3 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700">
    
    <!-- Barre de recherche principale -->
    <div class="relative">
      <label 
        for="search_input" 
        class="relative flex items-center bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-3 transition-all duration-200 border-2 border-transparent"
        :class="[
          isFocused ? 'bg-white dark:bg-slate-700 border-blue-500 dark:border-blue-400 shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-slate-700'
        ]"
      >
        <!-- Icône de recherche -->
        <div class="flex-shrink-0 mr-3">
          <i 
            class="las la-search text-lg transition-colors duration-200"
            :class="isFocused || filter.phone ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'"
          ></i>
        </div>

        <!-- Input de recherche -->
        <input 
          type="text" 
          id="search_input" 
          v-model="filter.phone"
          @focus="isFocused = true"
          @blur="isFocused = false"
          class="flex-1 appearance-none text-gray-700 dark:text-gray-200 outline-none border-none bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 text-sm font-medium"
          placeholder="Rechercher par numéro ou nom..."
          autocomplete="off"
        />

        <!-- Bouton clear -->
        <button
          v-if="filter.phone"
          @click="clearSearch"
          class="flex-shrink-0 ml-2 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center transition-all duration-200 hover:scale-110"
          type="button"
        >
          <i class="las la-times text-sm text-gray-600 dark:text-gray-300"></i>
        </button>

        <!-- Indicateur de chargement -->
        <div 
          v-if="isSearching" 
          class="flex-shrink-0 ml-2"
        >
          <div class="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin"></div>
        </div>
      </label>

      <!-- Compteur de résultats (optionnel) -->
      <div 
        v-if="filter.phone && searchResultCount !== null"
        class="absolute -bottom-6 left-0 right-0 text-center"
      >
        <span class="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full">
          {{ searchResultCount }} résultat{{ searchResultCount > 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Filtres rapides (optionnel) -->
    <div 
      v-if="showQuickFilters" 
      class="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
    >
      <button
        v-for="quickFilter in quickFilters"
        :key="quickFilter.value"
        @click="applyQuickFilter(quickFilter.value)"
        class="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200"
        :class="activeQuickFilter === quickFilter.value 
          ? 'bg-blue-500 text-white shadow-sm' 
          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'"
      >
        <i :class="quickFilter.icon" class="mr-1"></i>
        {{ quickFilter.label }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';

export default {
  name: "InboxSearch",
  
  data() {
    return {
      timeoutId: null,
      isFocused: false,
      isSearching: false,
      imageError: false,
      searchResultCount: null,
      activeQuickFilter: null,
      showQuickFilters: false, // Mettre à true si tu veux les filtres rapides
      quickFilters: [
        { label: 'Tous', value: 'all', icon: 'las la-list' },
        { label: 'Non lus', value: 'unread', icon: 'las la-envelope' },
        { label: 'Ouverts', value: 'open', icon: 'las la-folder-open' },
        { label: 'Clôturés', value: 'close', icon: 'las la-check-circle' }
      ]
    }
  },

  computed: {
    ...mapFields("conversations/list", {
      filter: "filter",
      conversations: "items"
    })
  },

  methods: {
    ...mapActions({
      getConversations: "conversations/list/getAll",
    }),

    debounceEffect() {
      // Clear le timeout existant
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      // Afficher l'indicateur de recherche
      this.isSearching = true;

      // Lancer la recherche après 400ms (réduit pour plus de réactivité)
      this.timeoutId = setTimeout(async () => {
        try {
          await this.getConversations();
          // Mettre à jour le compteur de résultats
          this.searchResultCount = this.conversations?.length ?? 0;
        } catch (error) {
          console.error('Erreur lors de la recherche:', error);
        } finally {
          this.isSearching = false;
        }
      }, 400);
    },

    clearSearch() {
      this.filter.phone = '';
      this.searchResultCount = null;
      this.activeQuickFilter = null;
    },

    applyQuickFilter(filterValue) {
      if (this.activeQuickFilter === filterValue) {
        // Désactiver le filtre si déjà actif
        this.activeQuickFilter = null;
        this.filter.status = null;
      } else {
        // Appliquer le nouveau filtre
        this.activeQuickFilter = filterValue;
        
        switch(filterValue) {
          case 'unread':
            this.filter.is_unread = true;
            break;
          case 'open':
            this.filter.status = 'open';
            break;
          case 'close':
            this.filter.status = 'close';
            break;
          default:
            this.filter.status = null;
            this.filter.is_unread = null;
        }
        
        this.getConversations();
      }
    }
  },

  watch: {
    "filter.phone"() {
      this.debounceEffect();
    }
  },

  beforeUnmount() {
    // Nettoyer le timeout si le composant est détruit
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar pour les filtres rapides */
.scrollbar-thin::-webkit-scrollbar {
  height: 4px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600 rounded-full;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-500;
}
</style>